import { Theme as MuiTheme } from '@mui/material/styles';
import { z } from 'zod';
import React from 'react';

/**
 * Wrap long text into multiple lines for chart axis labels
 */
export const wrapText = (text: string, maxLength = 26): string[] => {
	const words = text.split(' ');
	const lines: string[] = [];
	let currentLine = '';

	for (const word of words) {
		const nextLine = currentLine ? `${currentLine} ${word}` : word;
		if (nextLine.length > maxLength) {
			if (currentLine) lines.push(currentLine);
			currentLine = word;
		} else {
			currentLine = nextLine;
		}
	}
	if (currentLine) lines.push(currentLine);
	return lines;
};

/**
 * Create custom axis tick renderer with text wrapping
 */
export const createWrappedTickRenderer = (maxLength = 26, muiTheme?: MuiTheme) => {
	return (tick: { x: number; y: number; value: string | number }) => {
		const lines = wrapText(String(tick.value), maxLength);
		const textColor = muiTheme?.palette.text.secondary || '#666';

		return React.createElement('g', { transform: `translate(${tick.x},${tick.y})` },
			React.createElement('text', {
				textAnchor: 'end',
				dominantBaseline: 'middle',
				style: { fontSize: '12px', fill: textColor }
			},
				lines.map((line, i) =>
					React.createElement('tspan', {
						key: i,
						x: -10,
						dy: i === 0 ? 0 : 14
					}, line)
				)
			)
		);
	};
};

export const createNivoTheme = (muiTheme: MuiTheme) => ({
	background: 'transparent',
	text: {
		fontSize: 14,
		fill: muiTheme.palette.text.primary,
		fontFamily: muiTheme.typography.fontFamily,
	},
	axis: {
		domain: {
			line: {
				stroke: muiTheme.palette.divider,
				strokeWidth: 1,
			},
		},
		ticks: {
			line: {
				stroke: muiTheme.palette.divider,
				strokeWidth: 1,
			},
			text: {
				fill: muiTheme.palette.text.secondary,
				fontSize: 14,
			},
		},
		legend: {
			text: {
				fill: muiTheme.palette.text.primary,
				fontSize: 14,
				fontWeight: 600,
			},
		},
	},
	grid: {
		line: {
			stroke: muiTheme.palette.divider,
			strokeWidth: 1,
			strokeDasharray: '4 4',
		},
	},
	legends: {
		text: {
			fill: muiTheme.palette.text.primary,
			fontSize: 14,
		},
	},
	labels: {
		text: {
			fill: muiTheme.palette.mode === 'dark' ? '#ffffff' : '#000000',
			fontSize: 14,
			fontWeight: 600,
		},
	},
	tooltip: {
		container: {
			background: muiTheme.palette.background.paper,
			color: muiTheme.palette.text.primary,
			fontSize: 14,
			borderRadius: muiTheme.shape.borderRadius,
			boxShadow: muiTheme.shadows[3],
			padding: '8px 12px',
		},
	},
});

export const generateColorScheme = (count: number, muiTheme: MuiTheme): string[] => {
	const colors: string[] = [];
	const baseHue = 210;

	for (let i = 0; i < count; i++) {
		const hue = (baseHue + (i * 360) / count) % 360;
		const saturation = muiTheme.palette.mode === 'dark' ? 70 : 70;
		const lightness = muiTheme.palette.mode === 'dark' ? 60 : 50;
		colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
	}

	return colors;
};

export const getHighContrastColor = (muiTheme: MuiTheme): string => {
	return muiTheme.palette.mode === 'dark' ? '#ffffff' : '#000000';
};

export const ColorSchemeConfigSchema = z.object({
	id: z.string(),
	name: z.string(),
	colors: z.array(z.string())
}).strip();

export type ColorSchemeConfig = z.infer<typeof ColorSchemeConfigSchema>;

export const CHOROPLETH_COLOR_SCHEMES: ColorSchemeConfig[] = [
	{
		id: 'blue',
		name: 'Xanh dương',
		colors: ['#FFFFFF', '#90caf9', '#64b5f6', '#42a5f5', '#2196f3', '#1976d2']
	},
	{
		id: 'green',
		name: 'Xanh lá',
		colors: ['#FFFFFF', '#a5d6a7', '#66bb6a', '#4caf50', '#43a047', '#388e3c']
	},
	{
		id: 'orange',
		name: 'Cam',
		colors: ['#FFFFFF', '#ffcc80', '#ffb74d', '#ffa726', '#ff9800', '#fb8c00']
	},
	{
		id: 'teal',
		name: 'Xanh ngọc',
		colors: ['#FFFFFF', '#80cbc4', '#4db6ac', '#26a69a', '#009688', '#00897b']
	}
];

export const calculateLuminance = (hexColor: string): number => {
	const hex = hexColor.replace('#', '');
	const r = parseInt(hex.substr(0, 2), 16) / 255;
	const g = parseInt(hex.substr(2, 2), 16) / 255;
	const b = parseInt(hex.substr(4, 2), 16) / 255;

	const sRGB = [r, g, b].map(val => {
		return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
	});

	return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
};

export const getTextColorForBackground = (backgroundColor: string): string => {
	const luminance = calculateLuminance(backgroundColor);
	return luminance > 0.5 ? '#000000' : '#ffffff';
};
